import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import {
  type AiInsightsResponse,
  getAiInsightsAvailabilityMessage,
  useAiInsightJob,
} from "../../../api/aiInsights";
import { isApiError } from "../../../api/errors";
import { useGetPlanningInsightItems } from "../../../api/planningInsights";
import { useIsDataQualityVisible } from "../../../api/userAccess";
import { AiOutputFeedbackControls } from "../../aiOutputFeedback/AiOutputFeedbackControls";
import { Spinner } from "../../shared";
import { AiDataQualitySection } from "../AiDataQualitySection";
import {
  AiInsightDeveloperContext,
  AiInsightSourceMarker,
  AiInsightsRaidTopItemCard,
} from "../AiInsightsRaidDetailParts";
import { AiInsightWaitingState } from "../AiInsightWaitingState";
import { getInsightEvidenceRoute } from "../evidenceNavigation";
import { AiInsightsPlanningDetailModal } from "./AiInsightsPlanningDetailModal";
import type {
  PlanningAttentionItemDto,
  PlanningInsightDto,
  PlanningKeyRecommendationDto,
  PlanningPotentialUnrecordedConcernDto,
} from "./AiInsightsPlanningTypes";

const PERSONA_NOT_ASSIGNED_ERROR_CODE = "no_persona";

export type AiInsightsPlanningShelfProps = {
  isOpen: boolean;
  insightResponse?: AiInsightsResponse;
};

function itemLookupId(item: PlanningAttentionItemDto) {
  return item.planning_anchor?.anchor_id?.trim() || item.item_id?.trim() || "";
}

function formatAssessment(value?: string | null) {
  if (!value) return undefined;
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function PlanningRecommendationCard({
  item,
  planningItemsById,
}: {
  item: PlanningKeyRecommendationDto;
  planningItemsById: ReadonlyMap<string, import("../../../api/planningInsights").PlanningInsightItemDto>;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <article className="global-insights-overlay__raid-unrecorded-card global-insights-overlay__raid-unrecorded-card--ai-generated">
      <AiInsightSourceMarker />
      <button
        type="button"
        className="global-insights-overlay__raid-unrecorded-header"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((current) => !current)}
      >
        <div className="global-insights-overlay__raid-unrecorded-summary">
          <p className="global-insights-overlay__raid-unrecorded-meta">Recommended action</p>
          <h4 className="global-insights-overlay__raid-unrecorded-title">{item.title}</h4>
        </div>
        <span
          className={[
            "global-insights-overlay__raid-unrecorded-chevron",
            isExpanded ? "global-insights-overlay__raid-unrecorded-chevron--expanded" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          <span className="Appkit4-icon icon-down-chevron-outline" />
        </span>
      </button>
      {isExpanded && (
        <div className="global-insights-overlay__raid-unrecorded-content">
          <p className="global-insights-overlay__raid-unrecorded-label">Recommended action</p>
          <p className="global-insights-overlay__raid-unrecorded-text">{item.action}</p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Why this matters</p>
          <p className="global-insights-overlay__raid-unrecorded-text">{item.why_now}</p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">When</p>
          <p className="global-insights-overlay__raid-unrecorded-text">{item.when}</p>
          {item.related_item_ids.length > 0 && (
            <div className="global-insights-overlay__planning-related-items">
              <span className="global-insights-overlay__raid-unrecorded-tags-label">
                Related planning items:
              </span>
              {item.related_item_ids.map((relatedItemId) => (
                <a
                  key={relatedItemId}
                  className="global-insights-overlay__planning-related-item"
                  href={getInsightEvidenceRoute(relatedItemId, "planning")}
                  aria-label={`Open planning item ${relatedItemId}`}
                >
                  <span className="global-insights-overlay__planning-related-item-id">
                    ID: {relatedItemId}
                  </span>
                  <span className="global-insights-overlay__planning-related-item-title">
                    {planningItemsById.get(relatedItemId.toLowerCase())?.title ?? "Planning item"}
                  </span>
                  <span>
                    Owner: {planningItemsById.get(relatedItemId.toLowerCase())?.owner ?? "Not available"}
                  </span>
                  <span>
                    Status: {planningItemsById.get(relatedItemId.toLowerCase())?.status ?? "Not available"}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function PotentialConcernCard({ concern }: { concern: PlanningPotentialUnrecordedConcernDto }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="global-insights-overlay__raid-unrecorded-card global-insights-overlay__raid-unrecorded-card--ai-generated">
      <AiInsightSourceMarker />
      <button
        type="button"
        className="global-insights-overlay__raid-unrecorded-header"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
      >
        <div className="global-insights-overlay__raid-unrecorded-summary">
          <p className="global-insights-overlay__raid-unrecorded-meta">
            <span>Potential concern</span>
            <span className="global-insights-overlay__raid-unrecorded-confidence">
              {concern.confidence != null
                ? `Confidence: ${concern.confidence}`
                : "Needs validation"}
            </span>
          </p>
          <h4 className="global-insights-overlay__raid-unrecorded-title">
            {concern.title ?? "Potential concern"}
          </h4>
        </div>
        <span
          className={[
            "global-insights-overlay__raid-unrecorded-chevron",
            expanded ? "global-insights-overlay__raid-unrecorded-chevron--expanded" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          <span className="Appkit4-icon icon-down-chevron-outline" />
        </span>
      </button>
      {expanded && (
        <div className="global-insights-overlay__raid-unrecorded-content">
          <p className="global-insights-overlay__raid-unrecorded-text">
            {concern.description ?? "Not available"}
          </p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Why it matters</p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {concern.why_it_matters ?? "Not available"}
          </p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Validation required</p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {concern.validation_required ?? "Not available"}
          </p>
        </div>
      )}
    </div>
  );
}

/** Cached AI analysis plus a targeted authoritative Planning-record hydration. */
export function AiInsightsPlanningShelf({ isOpen, insightResponse }: AiInsightsPlanningShelfProps) {
  const isDataQualityVisible = useIsDataQualityVisible();
  const planningQuery = useAiInsightJob("planning", isOpen && insightResponse == null);
  const [selectedAttentionItem, setSelectedAttentionItem] =
    useState<PlanningAttentionItemDto | null>(null);
  const planningResponse = insightResponse ?? planningQuery.data?.data;
  const planningInsight = planningResponse?.insight as PlanningInsightDto | undefined;
  const topAttentionItems = useMemo(
    () =>
      (Array.isArray(planningInsight?.top_attention_items) ? planningInsight.top_attention_items : [])
        .slice(0, 5),
    [planningInsight]
  );
  const keyRecommendations = useMemo(
    () =>
      (Array.isArray(planningInsight?.key_recommendations_for_me)
        ? planningInsight.key_recommendations_for_me
        : []).slice(0, 3),
    [planningInsight]
  );
  const lookupIds = useMemo(
    () => [
      ...topAttentionItems.map(itemLookupId),
      ...keyRecommendations.flatMap((item) => item.related_item_ids ?? []),
    ].filter(Boolean),
    [keyRecommendations, topAttentionItems]
  );
  const planningLookup = useGetPlanningInsightItems(
    lookupIds,
    isOpen && planningInsight != null,
    planningResponse?.dealDataVersionId
  );
  const planningItemsById = useMemo(
    () =>
      new Map((planningLookup.data ?? []).flatMap((item) =>
        [item.planningItemRef, item.stableItemId, item.sourceActivityId, String(item.planningSnapshotItemId)]
          .filter((itemId): itemId is string => itemId != null && itemId.trim() !== "")
          .map((itemId) => [itemId.toLowerCase(), item] as const)
      )),
    [planningLookup.data]
  );

  const isPlanningLoading = isOpen && planningQuery.isLoading && planningInsight == null;
  const planningUnderlyingError = planningQuery.error;
  const isPersonaNotAssignedError =
    isApiError(planningUnderlyingError) &&
    planningUnderlyingError.code === PERSONA_NOT_ASSIGNED_ERROR_CODE;
  const isNoDataError =
    isApiError(planningUnderlyingError) && planningUnderlyingError.code === "no_data";
  const isReadinessWaiting = planningQuery.mappingReason != null;
  const planningError =
    isOpen && !isPlanningLoading && planningInsight == null
      ? isPersonaNotAssignedError
        ? "No AI Insights persona is assigned to your account yet."
        : planningQuery.isError && !isNoDataError && !isReadinessWaiting
          ? "Unable to load insights right now. Please try again shortly."
          : null
      : null;
  const planningUnavailableMessage =
    isOpen && !isPlanningLoading && planningInsight == null
      ? getAiInsightsAvailabilityMessage(
          isApiError(planningUnderlyingError) ? planningUnderlyingError.code : undefined,
          "Planning"
        )
      : null;
  const hasShownPersonaToastRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasShownPersonaToastRef.current = false;
      return;
    }
    if (isPersonaNotAssignedError && !hasShownPersonaToastRef.current) {
      hasShownPersonaToastRef.current = true;
      toast.error(
        "No AI Insights persona is assigned to your account yet. Contact an admin to get one assigned."
      );
    }
  }, [isOpen, isPersonaNotAssignedError]);

  if (isPlanningLoading)
    return (
      <section
        className="global-insights-overlay__section global-insights-overlay__section--loading"
        aria-live="polite"
      >
        <Spinner size="lg" />
      </section>
    );
  if (planningError != null)
    return (
      <section className="global-insights-overlay__section" aria-live="assertive">
        <p className="global-insights-overlay__raid-status-text global-insights-overlay__raid-status-text--error">
          {planningError}
        </p>
      </section>
    );
  if (planningUnavailableMessage != null)
    return isReadinessWaiting ? (
      <AiInsightWaitingState reason={planningQuery.mappingReason} />
    ) : (
      <section className="global-insights-overlay__section" aria-live="polite">
        <p className="global-insights-overlay__raid-status-text">{planningUnavailableMessage}</p>
      </section>
    );
  if (planningInsight == null) return null;

  const summary = planningInsight.summary;
  const dataQuality = planningInsight.data_quality_summary;
  const analysisBlocked = dataQuality?.impact_on_analysis === "analysis_blocked";
  const potentialConcerns = Array.isArray(planningInsight.potential_unrecorded_concerns)
    ? planningInsight.potential_unrecorded_concerns.slice(0, 3)
    : [];
  const dataQualityMetrics = [
    summary?.planning_freshness_status != null
      ? { label: "Plan data", value: formatAssessment(summary.planning_freshness_status) ?? summary.planning_freshness_status }
      : null,
    summary?.current_state_reliability != null
      ? { label: "Reliability", value: formatAssessment(summary.current_state_reliability) ?? summary.current_state_reliability }
      : null,
    summary?.data_coverage != null
      ? { label: "Coverage", value: formatAssessment(summary.data_coverage) ?? summary.data_coverage }
      : null,
  ].filter((metric): metric is { label: string; value: string } => metric != null);
  const selectedLookupId = selectedAttentionItem == null ? "" : itemLookupId(selectedAttentionItem);
  const selectedSourceItem =
    selectedLookupId ? planningItemsById.get(selectedLookupId.toLowerCase()) : undefined;

  return (
    <>
      <section
        className="global-insights-overlay__section global-insights-overlay__section--raid-health"
        aria-labelledby="global-insights-planning-title"
      >
        <div className="global-insights-overlay__section-header">
          <h3
            id="global-insights-planning-title"
            className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
          >
            Planning health
          </h3>
          <AiInsightSourceMarker />
        </div>
        <div className="global-insights-overlay__raid-health-card">
          {summary?.headline && (
            <p className="global-insights-overlay__planning-headline">{summary.headline}</p>
          )}
          <p className="global-insights-overlay__raid-health-text">
            {summary?.status_summary ?? "Not available"}
          </p>
          {analysisBlocked && (
            <p className="global-insights-overlay__raid-status-text">
              Current Planning content is unavailable because the available data does not support a
              reliable current conclusion.
            </p>
          )}
        </div>
        {planningResponse?.status === "ready" && (
          <AiOutputFeedbackControls
            executionId={planningResponse.aiOutputExecutionId}
            outputType="ai_insight"
            className="global-insights-overlay__feedback"
          />
        )}
      </section>
      {!analysisBlocked && (
        <section
          className="global-insights-overlay__section"
          aria-labelledby="global-insights-planning-items-title"
        >
          <h3
            id="global-insights-planning-items-title"
            className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
          >
            Top planning items
          </h3>
          {topAttentionItems.length > 0 ? (
            <div className="global-insights-overlay__raid-driver-list">
              {topAttentionItems.map((item, index) => {
                const lookupId = itemLookupId(item);
                const sourceItem = planningItemsById.get(lookupId.toLowerCase());
                return (
                  <AiInsightsRaidTopItemCard
                    key={`${lookupId || "planning-item"}-${index}`}
                    itemId={sourceItem?.planningItemRef ?? lookupId}
                    title={sourceItem?.title ?? item.item_name}
                    primaryMeta={sourceItem?.workstream ?? item.workstream}
                    secondaryMeta={sourceItem?.owner ?? item.owner}
                    primaryMetaLabel="Workstream"
                    secondaryMetaLabel="Owner"
                    metaLayout="aligned"
                    aiGenerated
                    onOpen={() => setSelectedAttentionItem(item)}
                  />
                );
              })}
            </div>
          ) : (
            <p className="global-insights-overlay__raid-status-text">
              No top planning items available.
            </p>
          )}
          {planningLookup.isError && (
            <p className="global-insights-overlay__raid-status-text">
              Current planning record details are unavailable; insight analysis is still shown.
            </p>
          )}
        </section>
      )}
      {!analysisBlocked && (
        <section
          className="global-insights-overlay__section"
          aria-labelledby="global-insights-planning-recommendations-title"
        >
          <h3
            id="global-insights-planning-recommendations-title"
            className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
          >
            Key recommendations for me
          </h3>
          {keyRecommendations.length > 0 ? (
            <div className="global-insights-overlay__planning-recommendation-list">
              {keyRecommendations.map((recommendation, index) => (
                <PlanningRecommendationCard
                  key={`${recommendation.title}-${index}`}
                  item={recommendation}
                  planningItemsById={planningItemsById}
                />
              ))}
            </div>
          ) : (
            <p className="global-insights-overlay__raid-status-text">
              No key recommendations available.
            </p>
          )}
        </section>
      )}
      {!analysisBlocked && potentialConcerns.length > 0 && (
        <section
          className="global-insights-overlay__section"
          aria-labelledby="global-insights-planning-validation-title"
        >
          <h3
            id="global-insights-planning-validation-title"
            className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
          >
            Potential concerns requiring validation
          </h3>
          <div className="global-insights-overlay__raid-unrecorded-list">
            {potentialConcerns.map((concern, index) => (
              <PotentialConcernCard
                key={`${concern.title ?? "concern"}-${index}`}
                concern={concern}
              />
            ))}
          </div>
        </section>
      )}
      {isDataQualityVisible && (
        <AiDataQualitySection
          headingId="global-insights-planning-quality-title"
          dataQuality={dataQuality}
          metrics={dataQualityMetrics}
          emptyStateDescription="The available planning data supports the current analysis."
        />
      )}
      <AiInsightDeveloperContext
        developerContext={{
          assessmentScope: summary?.assessment_scope,
          asAt: summary?.as_at_timestamp ?? summary?.planning_data_as_at,
          materialFlags: planningInsight.data_limitations?.material_flags,
          missingInputs: planningInsight.data_limitations?.missing_inputs,
          limitations: planningInsight.data_limitations?.limitations,
        }}
      />
      {selectedAttentionItem != null && (
        <AiInsightsPlanningDetailModal
          item={selectedAttentionItem}
          sourceItem={selectedSourceItem}
          onClose={() => setSelectedAttentionItem(null)}
        />
      )}
    </>
  );
}

export default AiInsightsPlanningShelf;
